import {
  allGuestNames,
  bookingArrival,
  dateKeyOf,
  fmtDate,
  fmtDateAndTime,
  fmtDateTime,
  getActualCheckInOut,
  roomLabel,
  STATUSES_WITH_ASSIGNED_ROOM,
} from "./adminHelpers";

/* The column order of the guest-house register sheet the "Copy details"
   button feeds. Kept beside the builder so the two can't drift apart. */
export const COPY_DETAILS_COLUMNS = [
  "Guest House",
  "Guest's Name",
  "Pax",
  "Department",
  "Period",
  "C/In",
  "C/Out",
  "Off / N. Off",
  "Purpose",
  "No. of Rooms",
  "Applicant's Name",
  "Contact No.",
  "Date",
  "Application",
  "Payment",
];

const MISSING = "-";

/* One spreadsheet cell: tabs and line breaks would split it into extra
   columns or rows on paste, and an empty or em-dash value reads as "-". */
function cell(value) {
  const s = String(value ?? "").replace(/[\t\r\n]+/g, " ").trim();
  return s && s !== "—" ? s : MISSING;
}

/* The expected (planned) departure: the booking's own, unless a room was
   extended or moved past it. Actual checkouts are C/Out's business. */
export function plannedDeparture(booking) {
  let date = booking.checkOut || "";
  let time = booking.departureTime || "";
  for (const a of booking.roomAssignments || []) {
    if (a.isNoShow || !a.effectiveDeparture) continue;
    const t = a.effectiveDepartureTime || time;
    if (`${a.effectiveDeparture} ${t}` > `${date} ${time}`) {
      date = a.effectiveDeparture;
      time = t;
    }
  }
  return { date, time };
}

/* The register writes the house by its short code with the rooms tacked on,
   "UGH-204-206"; one entry per house when a stay spans several. Before any
   room is allotted it is just the requested house's code. */
function guestHouseText(booking, ghShortById) {
  const shortOf = (id, name) => ghShortById?.get(id) || name || "";
  if (STATUSES_WITH_ASSIGNED_ROOM.includes(booking.status)) {
    const byHouse = new Map();
    for (const a of booking.roomAssignments || []) {
      if (!a.roomNumber || a.isNoShow) continue;
      const key = a.guestHouseShortName || shortOf(a.guestHouseId, a.guestHouseName);
      if (!byHouse.has(key)) byHouse.set(key, []);
      if (!byHouse.get(key).includes(a.roomNumber)) byHouse.get(key).push(a.roomNumber);
    }
    if (byHouse.size) {
      return [...byHouse].map(([gh, rooms]) => roomLabel(rooms.join("-"), gh)).join(", ");
    }
  }
  const pref = booking.guestHousePreferenceOptions?.[0];
  return pref ? shortOf(pref.id, pref.name) : shortOf(booking.guestHouseId, booking.guestHouseName);
}

/* "2 suite/1 room": each requested room type by its short name (full name
   when none is set), the way the register writes it. */
export function roomsText(booking) {
  const reqs = booking.guestHousePreferenceOptions?.[0]?.roomRequests || [];
  const text = reqs
    .filter((r) => r.count > 0)
    .map((r) => `${r.count} ${r.roomTypeShortName || r.roomTypeName}`.trim())
    .join("/");
  return text || booking.roomCount || (booking.guests ? String(booking.guests) : "");
}

function bookingTypeText(booking) {
  if (booking.bookingTypeCode === "OFFICIAL") return "Off";
  if (booking.bookingTypeCode === "NON_OFFICIAL" || booking.bookingTypeCode === "PERSONAL") return "N. Off";
  return booking.bookingType || "";
}

/* A booking as one tab-separated line, in COPY_DETAILS_COLUMNS order. Tabs
   rather than commas: Excel and Sheets split pasted text into columns on
   tabs, while a comma-separated line lands whole in a single cell.
   `paymentLabel` is the resolved payment-responsibility label the detail
   view already works out (the booking itself only carries the code). */
export function bookingDetailsRow(booking, { paymentLabel = "", ghShortById } = {}) {
  const b = booking || {};
  const { checkin, checkout } = getActualCheckInOut(b);
  const arr = bookingArrival(b, { planned: true });
  const dep = plannedDeparture(b);
  const period = arr.date || dep.date
    ? `${arr.date ? fmtDateAndTime(arr.date, arr.time) : "*"} - ${dep.date ? fmtDateAndTime(dep.date, dep.time) : "*"}`
    : "";
  const payment =
    b.paymentResponsibility === "OTHER" && b.paymentResponsibilityOther
      ? `${paymentLabel || "Other"}: ${b.paymentResponsibilityOther}`
      : paymentLabel || b.payment || "";
  const createdKey = dateKeyOf(b.createdAt);

  return [
    guestHouseText(b, ghShortById),
    allGuestNames(b).join(", "),
    1 + (Number(b.accompanyingPersonsCount) || 0),
    b.applicantDepartment || b.department,
    period,
    checkin ? fmtDateTime(checkin) : "",
    checkout ? fmtDateTime(checkout) : "",
    bookingTypeText(b),
    b.visitPurpose || b.purpose,
    roomsText(b),
    b.applicantName || b.userName,
    b.applicantMobileNumber || b.phone,
    createdKey ? fmtDate(createdKey) : "",
    b.isExternal ? "Namaste" : "Admin",
    payment,
  ]
    .map(cell)
    .join("\t");
}
